import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, Grid, IconButton, InputLabel, Select, Snackbar, TextField,
    Tooltip, Typography } from "@mui/material";
import { Search as IconSearch } from "@mui/icons-material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import DataGridBase from "../../components/common/DataGridBase";
import * as periodoService from "../../services/periodoService";
import comprobanteService from "../../services/comprobanteService";
import { getsTerapiaPeriodoResumenView, generarComprobanteTerapiaPeriodo } from "../../services/terapiaService";
import terapiaPeriodoPagoService from "../../services/terapiaPeriodoPagoService";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Pagina de mantenimiento del pago de un periodo de terapia.
//
// Los tres datos importantes son la terapia, el periodo y el comprobante: la
// terapia y el periodo seREFERencian con la lupa (buscando el periodo de terapia)
// y el comprobante con la lupa (buscando el comprobante y su item). El resto son
// datos que se muestran y se guardan.

const PaymentEdit = () => {
    const { isReadOnly, setIsReadOnly } = useMantenimiento("terapiaPeriodoPago");
    const { id, numero } = useParams();
    const navigate = useNavigate();

    const idTerapiaPeriodo = parseInt(id || "0");
    const numeroPago = parseInt(numero || "0");

    const nuevo = idTerapiaPeriodo <= 0 || numeroPago <= 0;

    const [entity, setEntity] = useState({
        idTerapiaPeriodo: 0,
        numero: 0,
        // Terapia: el participante y el terapeuta del periodo de terapia.
        participante: "",
        terapeuta: "",
        // Periodo de terapia.
        idPeriodo: 0,
        codigoPeriodo: "",
        estadoPeriodo: "",
        // Comprobante e item que paga el periodo.
        idComprobante: 0,
        serieNumero: "",
        estadoPago: "",
        idItem: 0,
        item: "",
        observaciones: "",
    });

    const [cargando, setCargando] = useState(true);

    const [listPeriodos, setListPeriodos] = useState([]);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    // Lupa de la terapia (periodos de terapia).
    const [buscandoPeriodo, setBuscandoPeriodo] = useState(false);
    const [filtrosPeriodo, setFiltrosPeriodo] = useState({ periodo: "", participante: "", terapeuta: "" });
    const [periodos, setPeriodos] = useState([]);

    // Lupa del comprobante.
    const [buscandoComprobante, setBuscandoComprobante] = useState(false);
    const [filtrosComprobante, setFiltrosComprobante] = useState({ serie: "", numero: "", pagador: "" });
    const [comprobantes, setComprobantes] = useState([]);
    const [comprobanteElegido, setComprobanteElegido] = useState(null);
    const [items, setItems] = useState([]);

    const mensaje = (severity, texto) => {
        setPropsMessage({ severity: severity, message: texto });
        setOpenMessage(true);
    };

    const fetchEntity = useCallback(async () => {
        if (nuevo) {
            setCargando(false);
            return;
        }

        setCargando(true);

        // Los datos de la terapia y del periodo salen del periodo de terapia.
        const data = await getsTerapiaPeriodoResumenView({
            idPeriodo: 0, idFilial: 0, idTipoTerapia: 0, participante: "",
            idTerapeuta: 0, terapeuta: "", idEstado: 0, idTerapiaPeriodo: idTerapiaPeriodo,
        }).catch((err) => { console.log("Err: ", err) });

        const periodo = (data || [])[0];

        // Los datos del pago (comprobante, item y observaciones).
        const pagos = await terapiaPeriodoPagoService.getsPagosDelPeriodo(idTerapiaPeriodo)
            .catch((err) => { console.log("Err: ", err) });

        const pago = (pagos || []).find(x => x.numero == numeroPago);

        setEntity({
            idTerapiaPeriodo: idTerapiaPeriodo,
            numero: numeroPago,
            participante: periodo ? periodo.participante : "",
            terapeuta: periodo ? periodo.terapeuta : "",
            idPeriodo: periodo ? periodo.idPeriodo : 0,
            codigoPeriodo: periodo ? periodo.codigoPeriodo : "",
            estadoPeriodo: periodo ? periodo.estadoApertura : "",
            idComprobante: pago ? pago.idComprobante : 0,
            serieNumero: pago ? (pago.serieNumero || "") : "",
            estadoPago: pago ? (pago.estadoPago || "") : "",
            idItem: pago ? pago.idItem : 0,
            item: pago ? (pago.concepto || "") : "",
            observaciones: pago ? (pago.observaciones || "") : "",
        });

        setCargando(false);
    }, [idTerapiaPeriodo, numeroPago, nuevo])

    const fetchListPeriodos = useCallback(async () => {
        const data = await periodoService.getsPeriodoView({
            idFilial: 0, idTipoTerapia: 0, idEstadoApertura: 0, mesesHaciaAtras: 0, idEstado: 0,
        }).catch((err) => { console.log("Err: ", err) });

        setListPeriodos(data || []);
    }, [])

    useEffect(() => {
        fetchEntity();
        fetchListPeriodos();
    }, [idTerapiaPeriodo, numeroPago])

    // ---------- Lupa de la terapia (periodos de terapia) ----------

    const buscarPeriodos = async () => {
        const data = await getsTerapiaPeriodoResumenView({
            idPeriodo: filtrosPeriodo.periodo == null || filtrosPeriodo.periodo == "" ? 0 : parseInt(filtrosPeriodo.periodo),
            idFilial: 0, idTipoTerapia: 0,
            participante: filtrosPeriodo.participante == null ? "" : filtrosPeriodo.participante,
            idTerapeuta: 0, terapeuta: filtrosPeriodo.terapeuta == null ? "" : filtrosPeriodo.terapeuta,
            idEstado: 0,
        }).catch((err) => { console.log("Err: ", err) });

        setPeriodos(data || []);
    }

    const elegirPeriodo = async (periodo) => {
        // El pago nuevo del periodo va con el numero siguiente al ultimo.
        const pagos = await terapiaPeriodoPagoService.getsPagosDelPeriodo(periodo.id)
            .catch((err) => { console.log("Err: ", err) });

        const siguiente = (pagos || []).reduce((maximo, pago) => Math.max(maximo, pago.numero), 0) + 1;

        setEntity((prev) => ({
            ...prev,
            idTerapiaPeriodo: periodo.id,
            numero: siguiente,
            participante: periodo.participante,
            terapeuta: periodo.terapeuta,
            idPeriodo: periodo.idPeriodo,
            codigoPeriodo: periodo.codigoPeriodo,
            estadoPeriodo: periodo.estadoApertura,
            // El comprobante se elige con la otra lupa.
            idComprobante: 0,
            serieNumero: "",
            estadoPago: "",
            idItem: 0,
            item: "",
        }));

        setBuscandoPeriodo(false);
    }

    // ---------- Lupa del comprobante ----------

    const abrirBusquedaComprobante = async () => {
        setBuscandoComprobante(true);
        setComprobanteElegido(null);
        setItems([]);

        const data = await comprobanteService.getsComprobanteResumenViewFilter({
            fechaInicio: null, fechaFin: null, idTipo: 0,
            serie: filtrosComprobante.serie == null ? "" : filtrosComprobante.serie,
            numero: filtrosComprobante.numero == null ? "" : filtrosComprobante.numero,
            dniCobrador: "", cobrador: "", dniPagador: "",
            pagador: filtrosComprobante.pagador == null ? "" : filtrosComprobante.pagador,
            idEstadoPago: 0, idEstado: 2,
        }).then((response) => { return response.data.data })
            .catch((err) => { console.log("Err: ", err) });

        setComprobantes(data || []);
    }

    const verItems = async (comprobante) => {
        const response = await comprobanteService.getsComprobanteDetalleView(comprobante.id)
            .catch((err) => { console.log("Err: ", err) });

        setComprobanteElegido(comprobante);
        setItems((response && response.data && response.data.data) || []);
    }

    // Referenciar el comprobante (y su item) al pago del periodo de terapia.
    const elegirItem = (item) => {
        setEntity((prev) => ({
            ...prev,
            idComprobante: comprobanteElegido.id,
            serieNumero: comprobanteElegido.serieNumero,
            estadoPago: comprobanteElegido.estadoPago,
            idItem: item.numero,
            item: "Item " + item.numero + " - " + item.conceptoCobro,
        }));

        setBuscandoComprobante(false);
        setComprobanteElegido(null);
        setItems([]);
    }

    const cerrarBusquedaComprobante = () => {
        setBuscandoComprobante(false);
        setComprobanteElegido(null);
        setItems([]);
    }

    // ---------- Guardar ----------

    const guardar = async () => {
        if (entity.idTerapiaPeriodo <= 0) {
            mensaje("error", "Elija la terapia y el periodo del pago.");
            return;
        }

        if (entity.idComprobante <= 0 || entity.idItem <= 0) {
            mensaje("error", "Elija el comprobante y el item que paga el periodo.");
            return;
        }

        await terapiaPeriodoPagoService.addTerapiaPeriodoPago({
            idTerapiaPeriodo: entity.idTerapiaPeriodo,
            numero: entity.numero,
            idComprobante: entity.idComprobante,
            idItem: entity.idItem,
            observaciones: entity.observaciones == null ? "" : entity.observaciones,
        })
            .then(() => {
                setPropsMessage({ severity: "success", message: "Se guardo el pago del periodo con exito." });
                setOpenMessage(true);
                setIsReadOnly(true);
                navigate("/therapyperiod/pago/edit/" + entity.idTerapiaPeriodo + "/" + entity.numero);
            })
            .catch((err) => {
                console.log("Err: ", err);
                mensaje("error", "No se pudo guardar el pago del periodo.");
            });
    }

    // Genera el comprobante del periodo cuando no tiene ningun pago activo.
    const generarComprobante = async () => {
        if (entity.idTerapiaPeriodo <= 0) {
            mensaje("warning", "Elija primero la terapia y el periodo.");
            return;
        }

        await generarComprobanteTerapiaPeriodo(entity.idTerapiaPeriodo)
            .then((idComprobante) => {
                if (idComprobante <= 0) {
                    mensaje("warning", "El periodo ya tiene un pago activo: anulelo si necesita generar otro.");
                    return;
                }

                mensaje("success", "Se genero el comprobante del periodo. Elijalo con la lupa para referenciarlo.");
            })
            .catch((err) => {
                console.log("Err: ", err);
                mensaje("error", "No se pudo generar el comprobante del periodo.");
            });
    }

    const columnasPeriodos = [
        { field: 'codigoPeriodo', headerName: 'Periodo', width: 110, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'participante', headerName: 'Participante', width: 190, sortable: false, headerAlign: 'left' },
        { field: 'terapeuta', headerName: 'Terapeuta', width: 160, sortable: false, headerAlign: 'left' },
        { field: 'estadoApertura', headerName: 'Apertura', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'serieNumero', headerName: 'Comprobante', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'elegir', headerName: '', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => elegirPeriodo(cellValues.row)}>
                    {"Elegir"}
                </Box>
            )
        },
    ];

    const columnasComprobantes = [
        { field: 'serieNumero', headerName: 'Comprobante', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fecha', headerName: 'Fecha', width: 110, type: 'date'
            , valueFormatter: (value) => moment(value).format('D/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'pagador', headerName: 'Pagador', width: 190, sortable: false, headerAlign: 'left' },
        { field: 'montoTotal', headerName: 'Total', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'montoSaldo', headerName: 'Saldo', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'items', headerName: '', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => verItems(cellValues.row)}>
                    {"Ver items"}
                </Box>
            )
        },
    ];

    const columnasItems = [
        { field: 'numero', headerName: 'Item', width: 60, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'cantidad', headerName: 'Cantidad', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'conceptoCobro', headerName: 'Concepto', width: 250, sortable: false, headerAlign: 'left' },
        { field: 'subTotal', headerName: 'Sub Total', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'elegir', headerName: '', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => elegirItem(cellValues.row)}>
                    {"Elegir"}
                </Box>
            )
        },
    ];

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Pago de Periodo de Terapia"}
                backTo={"/therapyperiod/pago"}
                onSave={guardar}
                disabled={isReadOnly}
            >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                {/* Terapia y periodo: los tres datos importantes, con
                                    la lupa para referenciarlos. */}
                                <Grid container spacing={1} alignItems={"center"}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            disabled
                                            name="participante"
                                            label="Terapia"
                                            value={entity.participante}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            disabled
                                            name="codigoPeriodo"
                                            label="Periodo"
                                            value={entity.codigoPeriodo}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} display={"flex"} justifyContent={"flex-end"}>
                                        <Tooltip title={nuevo ? "Buscar terapia y periodo" : "Cambiar la terapia y el periodo"}>
                                            <IconButton aria-label="buscar terapia" onClick={() => { setBuscandoPeriodo(true); setPeriodos([]); }}>
                                                <IconSearch />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={2} display={"flex"} justifyContent={"flex-end"}>
                                        <Button
                                            variant="outlined"
                                            disabled={isReadOnly || entity.idTerapiaPeriodo <= 0}
                                            onClick={generarComprobante}
                                        >
                                            {"Generar"}
                                        </Button>
                                    </Grid>
                                </Grid>

                                {/* Comprobante e item que paga el periodo. */}
                                <Grid container spacing={1} alignItems={"center"}>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="numero"
                                            label="N° Pago"
                                            value={String(entity.numero).padStart(3, "0")}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            disabled
                                            name="serieNumero"
                                            label="Comprobante"
                                            value={entity.serieNumero}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} display={"flex"} justifyContent={"flex-end"}>
                                        <Tooltip title={"Buscar comprobante"}>
                                            <IconButton aria-label="buscar comprobante"
                                                disabled={isReadOnly || entity.idTerapiaPeriodo <= 0}
                                                onClick={abrirBusquedaComprobante}>
                                                <IconSearch />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            disabled
                                            name="item"
                                            label="Item"
                                            value={entity.item}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            disabled
                                            name="estadoPago"
                                            label="Estado Pago"
                                            value={entity.estadoPago}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            name="observaciones"
                                            label="Observaciones"
                                            value={entity.observaciones}
                                            fullWidth
                                            multiline
                                            disabled={isReadOnly}
                                            onChange={(event) => setEntity({ ...entity, observaciones: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Lupa de la terapia: busca el periodo de terapia del pago. */}
            <Dialog open={buscandoPeriodo} fullWidth maxWidth={"md"} onClose={() => setBuscandoPeriodo(false)}>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {"Buscar Terapia y Periodo"}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px", paddingBottom: "12px" }}>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-periodobusqueda">Periodo</InputLabel>
                                <Select
                                    native
                                    label="Periodo"
                                    value={filtrosPeriodo.periodo}
                                    onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, periodo: event.target.value })}
                                    inputProps={{ name: 'idPeriodo', id: 'input-periodobusqueda' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    {listPeriodos.map((row) => (
                                        <option key={row.id} value={row.id}>{row.codigo}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} sm={3}>
                            <TextField name="participante" label="Participante" value={filtrosPeriodo.participante} fullWidth
                                onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, participante: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarPeriodos(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={3}>
                            <TextField name="terapeuta" label="Terapeuta" value={filtrosPeriodo.terapeuta} fullWidth
                                onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, terapeuta: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarPeriodos(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Tooltip title={"Buscar periodos de terapia"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { buscarPeriodos() }}>
                                    <IconSearch />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase rows={periodos} columns={columnasPeriodos} loading={cargando} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={() => setBuscandoPeriodo(false)}>{"Cerrar"}</Button>
                </DialogActions>
            </Dialog>

            {/* Lupa del comprobante: se elige el comprobante y su item para
                referenciarlos al pago del periodo de terapia. */}
            <Dialog open={buscandoComprobante} fullWidth maxWidth={"md"} onClose={cerrarBusquedaComprobante}>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {"Buscar Comprobante"}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px", paddingBottom: "12px" }}>
                        <Grid item xs={4} sm={2}>
                            <TextField name="serie" label="Serie" value={filtrosComprobante.serie} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, serie: event.target.value })}
                                size="small" />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                            <TextField name="numero" label="Numero" value={filtrosComprobante.numero} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, numero: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); abrirBusquedaComprobante(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={3}>
                            <TextField name="pagador" label="Pagador" value={filtrosComprobante.pagador} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, pagador: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); abrirBusquedaComprobante(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Tooltip title={"Buscar comprobantes"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { abrirBusquedaComprobante(); }}>
                                    <IconSearch />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    {comprobanteElegido
                        ? <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <Alert severity={"info"} sx={{ marginBottom: "8px" }}>
                                {"Elija el item del comprobante " + comprobanteElegido.serieNumero + " que paga el periodo."}
                            </Alert>
                            <DataGridBase rows={items} columns={columnasItems} />
                        </Box>
                        : <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase rows={comprobantes} columns={columnasComprobantes} />
                        </Box>
                    }
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={cerrarBusquedaComprobante}>{"Cerrar"}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default PaymentEdit;
